import React, { createContext, useContext } from 'react';
import { Result } from 'ahooks/lib/useRequest/src/types';

import {
  useRequest,
  getInsuranceSubmissionFlowUrl,
  getInsuranceSubmissionFlowTransitionUrl,
  logger,
  toast,
  useAuth,
  INSURANCE_CARD_STATUSES,
  InsuranceSubmissionFlowStageEnum,
  PatientInsuranceCardDto,
  getPatientInsuranceCards,
} from '@monument/core';
import { shouldUpdateInsurance } from './useInsurance.utils';
import { InsuranceDiscoveryInfoFormPayload } from '@shared/components/insurance-submission-flow/discovery-info/InsuranceDiscoveryInfoForm';

interface InsuranceFlowDto {
  id: number;
  patientId: string;
  createdAt: string;
  updatedAt: string;
  stage: InsuranceSubmissionFlowStageEnum | undefined;
  cardId: number;
  store: { patientOnboardingId: number };
  card: PatientInsuranceCardDto;
}

export interface InsuranceShortcutInfoFormPaylod {
  payer: string;
  insuranceMemberId: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  forceTransition?: boolean;
}

interface InsuranceCardImagesPayload {
  insuranceCardFrontImage?: string;
  insuranceCardBackImage?: string;
}

type InsuranceSubmissionPayload =
  | InsuranceDiscoveryInfoFormPayload
  | InsuranceShortcutInfoFormPaylod
  | InsuranceCardImagesPayload;

interface InsuranceSubmission {
  flow: Result<InsuranceFlowDto, []>;
  stage: InsuranceSubmissionFlowStageEnum | undefined;
  initFlowLoading: boolean;
  initFlow: () => Promise<void>;
  transitionLoading: boolean;
  transition: (
    event: string,
    payload?: InsuranceSubmissionPayload,
    options?: {
      onBeforeSync?: (newFlow: InsuranceFlowDto) => Promise<unknown>;
      onAfterSync?: (newFlow: InsuranceFlowDto) => Promise<unknown>;
    },
  ) => Promise<void>;
}

interface IInsuranceContext {
  submission: InsuranceSubmission;
  cards: Result<PatientInsuranceCardDto[], []>;
  activeCard: PatientInsuranceCardDto | undefined;

  /**
   * Patient should update insurance if:
   * 1) Patient has no active insurance card.
   * 2) Patient insurance card submissions are all ineligible.
   * 3) Patient has some insurance card submissions that were unenrolled within a
   * designated time threshold.
   */
  shouldUpdateInsurance: boolean;
  refresh: () => Promise<unknown>;
  loadingRefresh: boolean;
}

const InsuranceContext = createContext<IInsuranceContext>({} as IInsuranceContext);
export const useInsurance = () => useContext(InsuranceContext);

const useInsuranceSubmissionFlow = (): InsuranceSubmission => {
  const { refetchUser } = useAuth();

  const flowReq = useRequest<InsuranceFlowDto, []>(
    {
      url: getInsuranceSubmissionFlowUrl(),
      withErrorsHandler: false,
    },
    {
      manual: true,
    },
  );

  const initFlowReq = useRequest(
    () => {
      return {
        method: 'post',
        url: getInsuranceSubmissionFlowUrl(),
        withErrorsHandler: false,
      };
    },
    {
      manual: true,
    },
  );

  const transitionReq = useRequest<
    InsuranceFlowDto,
    [
      event: string,
      payload: InsuranceSubmissionPayload | undefined,
      options: {
        handleErrors?: boolean;
        onBeforeSync?: (newFlow: InsuranceFlowDto) => Promise<unknown>;
        onAfterSync?: (newFlow: InsuranceFlowDto) => Promise<unknown>;
      },
    ]
  >(
    (event, payload) => ({
      url: getInsuranceSubmissionFlowTransitionUrl(),
      method: 'post',
      data: {
        event,
        payload: {
          ...payload,
          id: flowReq?.data?.id,
        },
      },
      withErrorsHandler: false,
    }),
    {
      manual: true,
      onError: (error, [, , options = { handleErrors: true }]) => {
        if (options.handleErrors) {
          // Invalid state transitions shouldn't be possible if everything is working correctly. On the
          // off-chance a user gets into an invalid state, we should restore to whatever stage the
          // backend thinks we should be in.
          logger.error('useInsuranceSubmissionFlow#transition', error);
          flowReq.refresh();
          toast.error({
            title: 'Something went wrong',
            description:
              'Please reload the page and try again, or contact support if the problem continues.',
          });
        }
      },
    },
  );

  const transition = async (
    event: string,
    payload: InsuranceSubmissionPayload | undefined,
    options: {
      handleErrors?: boolean;
      onBeforeSync?: (newFlow: InsuranceFlowDto) => Promise<unknown>;
      onAfterSync?: (newFlow: InsuranceFlowDto) => Promise<unknown>;
    } = {},
  ) => {
    const newFlow = await transitionReq.runAsync(event, payload, options);

    refetchUser();

    // Transitions may mutate other application data. Allow consumers to provide an onBeforeSync
    // callback for any necessary cache-busting (or other followups) before mutating the
    // client's source-of-truth nsuracneSubmissionFlow object.
    if (options.onBeforeSync) {
      await options.onBeforeSync(newFlow);
    }

    flowReq.mutate(newFlow);

    if (options.onAfterSync) {
      await options.onAfterSync(newFlow);
    }
  };

  const initFlow = async () => {
    await initFlowReq.runAsync();
    await flowReq.runAsync();
    initFlowReq.mutate(null);
  };

  return {
    flow: flowReq,
    stage: flowReq.data?.stage,
    initFlowLoading: initFlowReq.loading,
    initFlow,
    transitionLoading: transitionReq.loading,
    transition,
  };
};

export const InsuranceContextProvider = ({ children }) => {
  const submission = useInsuranceSubmissionFlow();
  const cards = useRequest<PatientInsuranceCardDto[]>(getPatientInsuranceCards('me'), {
    manual: true,
    refreshDeps: [submission.flow.data],
  });
  const activeCard = cards.data?.find(card => card.status === INSURANCE_CARD_STATUSES.ACTIVE);

  return (
    <InsuranceContext.Provider
      value={{
        submission,
        cards,
        activeCard,
        shouldUpdateInsurance: shouldUpdateInsurance(cards.data),
        refresh: async () => Promise.all([submission.flow.refreshAsync(), cards.refreshAsync()]),
        loadingRefresh: cards.loading || submission.flow.loading,
      }}
    >
      {children}
    </InsuranceContext.Provider>
  );
};
